import {Signal, signal, WritableSignal} from "@angular/core";
import {WalletPaymentErrorType, WalletPaymentSubmissionFailure, WalletPaymentSubmissionResult, WalletProvider} from '../payment';
import {PaymentModel} from "../payment.model";
import {ConfigurationModel, ParamConfig} from '../../configuration/configuration.model';

export abstract class WalletPaymentProcessor {
    private static readonly WALLET_PROVIDER_RECHECK_INTERVAL_MS: number = 15000;
    private static readonly WALLET_PROVIDER_RECHECK_TIMEOUT_MS: number = 5 * 60 * 1000;

    private readonly _walletProvider: WritableSignal<WalletProvider> = signal<WalletProvider>(undefined);
    public readonly walletProvider: Signal<WalletProvider> = this._walletProvider.asReadonly();

    public abstract readonly quickPayAvailable: Signal<boolean>;
    public abstract readonly canSubmit: Signal<boolean>;

    protected resolveSubmission: (value?: WalletPaymentSubmissionResult | PromiseLike<WalletPaymentSubmissionResult>) => void;
    protected rejectSubmission: (reason?: WalletPaymentSubmissionFailure) => void;

    private disposed: boolean = false;
    private walletProviderRecheckTimer: any;
    private walletProviderRecheckDeadline: number;

    protected constructor(protected paymentModel: PaymentModel, protected configurationModel: ConfigurationModel) {
    }

    public abstract init(): Promise<WalletPaymentProcessor>;

    /**
     * Stops any pending background recheck (see resolveWalletProvider() below) - callers replacing this
     * processor with a new one must call this, or the old instance's recheck loop runs indefinitely, kept
     * alive forever by its own pending timer even though nothing references the processor anymore.
     */
    public dispose(): void {
        this.disposed = true;
        clearTimeout(this.walletProviderRecheckTimer);
    }

    /**
     * Provider-specific availability check - just the check itself, not the forcePay override or retry
     * logic, both handled once by resolveWalletProvider() below for every implementation. Return
     * `undefined` when the provider can positively rule wallet payment out (e.g. the platform simply
     * doesn't have the required component), or `null` when it can't tell "not available" apart from "not
     * finished checking yet" - the latter is retried, the former never is.
     */
    protected abstract determineWalletProvider(): Promise<WalletProvider | null | undefined>;

    /**
     * Called immediately whenever walletProvider is set - a no-op by default. Override for other derived
     * state that depends on walletProvider too (e.g. Tyro's canSubmit).
     */
    protected onWalletProviderChanged(): void {
    }

    /**
     * An "unsure yet" (null) result can be a false negative if the browser/OS's own wallet discovery
     * hasn't finished yet, so it schedules a background recheck every WALLET_PROVIDER_RECHECK_INTERVAL_MS
     * until a provider is found, or WALLET_PROVIDER_RECHECK_TIMEOUT_MS elapses and it gives up. A
     * definite "not available" (undefined) is never retried.
     */
    protected async resolveWalletProvider(): Promise<void> {
        const paramConfig: ParamConfig = await this.paymentModel.configurationModel.getParamConfig();
        const provider: WalletProvider | null | undefined = paramConfig.forcePay === "apple" ? WalletProvider.APPLE_PAY
            : paramConfig.forcePay === "google" ? WalletProvider.GOOGLE_PAY
                : await this.determineWalletProvider();
        if (provider) {
            this.setWalletProvider(provider);
        } else if (provider === null) {
            this.walletProviderRecheckDeadline = Date.now() + WalletPaymentProcessor.WALLET_PROVIDER_RECHECK_TIMEOUT_MS;
            this.scheduleWalletProviderRecheck();
        } else {
            this.setWalletProvider(undefined);
        }
    }

    private scheduleWalletProviderRecheck(): void {
        this.walletProviderRecheckTimer = setTimeout(async () => {
            if (this.disposed) {
                return;
            }
            const provider: WalletProvider | null | undefined = await this.determineWalletProvider();
            if (provider) {
                this.setWalletProvider(provider);
            } else if (provider === null && Date.now() < this.walletProviderRecheckDeadline) {
                this.scheduleWalletProviderRecheck();
            } else {
                console.log("Giving up on wallet payment provider detection");
                this.setWalletProvider(undefined);
            }
        }, WalletPaymentProcessor.WALLET_PROVIDER_RECHECK_INTERVAL_MS);
    }

    private setWalletProvider(provider: WalletProvider): void {
        console.log("Wallet payment provider =", provider);
        this._walletProvider.set(provider);
        this.onWalletProviderChanged();
    }

    // noinspection JSUnusedLocalSymbols
    public async submit(amount: number, deferPaymentSheet: boolean = false): Promise<WalletPaymentSubmissionResult> {
        if (!this.canSubmit()) {
            throw new WalletPaymentSubmissionFailure(WalletPaymentErrorType.INVALID_STATE);
        }

        if (this.resolveSubmission) {
            this.rejectSubmission && this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.INVALID_STATE));
            await this.init();
        }

        void this.paymentModel.analyticsService.trackWalletPaymentRequestStarted(this.walletProvider());

        return new Promise<WalletPaymentSubmissionResult>((resolve: (value?: WalletPaymentSubmissionResult | PromiseLike<WalletPaymentSubmissionResult>) => void, reject: (reason?: any) => void) => {
            this.resolveSubmission = (val?: WalletPaymentSubmissionResult | PromiseLike<WalletPaymentSubmissionResult>) => {
                this.resolveSubmission = undefined;
                this.rejectSubmission = undefined;
                resolve(val);
            };

            this.rejectSubmission = (reason?: any) => {
                this.resolveSubmission = undefined;
                this.rejectSubmission = undefined;

                // Detect transient activation errors and set a specific error type to trigger retry via a confirmation popup
                if (reason instanceof WalletPaymentSubmissionFailure && reason.providerErrorMessage &&
                    ((this.walletProvider() === WalletProvider.APPLE_PAY && reason.providerErrorMessage.includes("The code that shows the Apple Pay payment sheet must be invoked directly by a user activation event")) ||
                        (this.walletProvider() === WalletProvider.GOOGLE_PAY && reason.providerErrorMessage.includes("Delegation is not allowed without transient user activation")))) {
                    reason.errorType = WalletPaymentErrorType.TRANSIENT_ACTIVATION;
                }

                reject(reason);
            };

            return deferPaymentSheet ? this.paymentModel.showWalletPaymentSheetDeferred(amount) : this.showPaymentSheet(amount);
        });
    }

    public abstract showPaymentSheet(amount: number): Promise<void>;
}
